import { Component, Input, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Select } from '@ngxs/store';
import { Observable } from 'rxjs/Observable';
import { CompanyData } from '../../../models/agents/company-data';
import { DomainService } from '../../../services/domain.service';
import { RegistrationService } from '../../../services/registration.service';
import { CompanyState } from '../../../state-manager/state/company-register.state';
import { MultipleEmail } from '../../../models/agents/multiple-email';
import { DomainTypes } from '../../../models/domain/domain-types';
import { StatesDomain } from '../../../models/domain/states-domain';
import { CitiesDomain } from '../../../models/domain/cities-domain';
import { AgentFullRegistration } from '../../../models/agents/agent-full-registration';
import { DomainType } from '../../../models/enums/domain-enum';
import { InvestorDataControlRegisterSections } from '../../company/register/investor-data/investor-data.rules';
import { InvestorCompanyInvestorDataFormGroup } from '../../company/register/investor-data/forms/investor-company.form-group';
import { InvestmentFundInvestorDataFormGroup } from '../../company/register/investor-data/forms/investment-fund-form-group';
import { ManagerInvestorDataFormGroup } from '../../company/register/investor-data/forms/manager.form-group';
import { ToastyService } from 'ng2-toasty';
import { RxFormBuilder } from '@rxweb/reactive-form-validators';
import { PersonType } from '../../../models/enums/person-type.enum';

@Component({
  selector: 'app-company-basic-data',
  templateUrl: './company-basic-data.component.html',
  styleUrls: ['./company-basic-data.component.scss']
})
export class CompanyBasicDataComponent
  extends InvestorDataControlRegisterSections
  implements OnInit {
  @Input() registration: AgentFullRegistration = {};
  @Input() loading = false;
  @Select(CompanyState.company) company$: Observable<CompanyData>;

  emails: Array<MultipleEmail> = [];
  formGroup: FormGroup;
  countries: Observable<Array<DomainTypes>>;
  states: Observable<Array<StatesDomain>>;
  cities: Observable<CitiesDomain[]>;
  showCompanyCountrySection = false;

  constructor(
    protected route: ActivatedRoute,
    protected registrationService: RegistrationService,
    protected domainService: DomainService,
    protected formBuilder: FormBuilder,
    protected investorFundFormGroup: InvestorCompanyInvestorDataFormGroup,
    protected InvestmentFundInvestorDataFormGroup: InvestmentFundInvestorDataFormGroup,
    protected ManagerInvestorDataFormGroup: ManagerInvestorDataFormGroup,
    protected toastyService: ToastyService,
    protected rxFormBuilder: RxFormBuilder
  ) {
    super(
      formBuilder,
      toastyService,
      investorFundFormGroup,
      InvestmentFundInvestorDataFormGroup,
      ManagerInvestorDataFormGroup,
      rxFormBuilder
    );
    this.countries = this.domainService.get(DomainType.COUNTRY);
    this.states = this.domainService.getStates();
  }

  ngOnInit() {}

  initReactiveForms(callFromNgInit: boolean = false): void {
    if (!callFromNgInit) {
      return;
    }
    this.formGroup = this.getCompanyFormGroupInvestorData(
      this.registration.agent
    );

    this.formGroup
      .get('fullRegister')
      .valueChanges.subscribe((fullRegister) =>
        this.setFieldsOptional(fullRegister)
      );
  }

  setFieldsOptional(fullRegister: boolean): void {
    const optionalFieldsNotFullRegister = [
      'responsibleAddress.address.street',
      'responsibleAddress.address.district',
      'responsibleAddress.address.state',
      'responsibleAddress.address.city',
      'responsibleAddress.partType',
      'responsibleAddress.address.zipCode',
      'email',
      'responsibleAddress.address.number'
    ];

    optionalFieldsNotFullRegister.forEach((control) => {
      const ctrl = <FormControl>this.formGroup.get(control);

      if (ctrl) {
        if (fullRegister) {
          this.makeRequired(ctrl);
        } else {
          this.makeOptional(ctrl);
        }
      }
    });
  }

  makeRequired(formControl: FormControl) {
    formControl.setValidators(Validators.required);
    formControl.updateValueAndValidity();
  }

  makeOptional(formControl: FormControl) {
    formControl.setValidators([]);
    formControl.updateValueAndValidity();
  }

  ngOnChanges() {
    this.initReactiveForms(true);
    this.setRegistration();
  }

  get isEscrowOrBancoLiquidante() {
    return (
      this.registration &&
      this.registration.agent &&
      (this.registration.agent.isEscrowTitular() ||
        this.registration.agent.isEscrowPartesRelacionadas() ||
        this.registration.agent.isBancoLiquidantePartesRelacionadas() ||
        this.registration.agent.isBancoLiquidanteTitular())
    );
  }

  get isEscrow() {
    return (
      this.registration &&
      this.registration.agent &&
      (this.registration.agent.isEscrowTitular() ||
        this.registration.agent.isEscrowPartesRelacionadas())
    );
  }

  get isFundoInvestimento() {
    return (
      this.registration &&
      this.registration.agent &&
      this.registration.agent.personType === PersonType.Juridica
    );
  }

  setRegistration() {
    if (this.registration) {
      const company = this.registration.company;

      if (
        this.registration.company &&
        this.registration.company.headOfficeAddress
      ) {
        this.registration.company.headOfficeAddress.hasNoNumber =
          this.hasNoNumber(this.registration.company);
      }

      this.formGroup.patchValue({ ...company });
      this.setEmails(company);
    }
  }

  hasNoNumber(company: CompanyData): boolean {
    return !company.headOfficeAddress || !company.headOfficeAddress.number;
  }

  zipCodeToString(zipCode: number): string {
    if (!zipCode) {
      return '';
    }

    return '0'.repeat(8 - zipCode.toString().length) + '' + zipCode;
  }

  setEmails(company: CompanyData) {
    if (company.email) {
      this.emails = company.email
        .split(',')
        .map((email) => ({ display: email, value: email }));
    }
  }

  translateBoolFormGroupValue(formName: string): string {
    return this.formGroup.get(formName) && this.formGroup.get(formName).value
      ? 'Sim'
      : 'Não';
  }
}
